import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  Card,
  Divider,
  Chip,
  Container,
  TextField,
  Paper,
  Alert,
  CardContent,
  Pinterest,
} from '../utils/mui-imports';
import Footer from '../components/FooterNewUI';
import PinterestPinCarousel from '../components/PinterestPinCarousel';

const PinterestStylePinPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [navigationLoading, setNavigationLoading] = useState(false);
  const [error, setError] = useState('');
  const [pinterestEmbed, setPinterestEmbed] = useState('');
  const [extractedInfo, setExtractedInfo] = useState(null);
  const [processingUrl, setProcessingUrl] = useState(false);
  const [selectedStyleOption, setSelectedStyleOption] = useState('color');

  // Handle pinUrl query parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const pinUrl = urlParams.get('pinUrl');
    if (pinUrl) {
      setUrl(pinUrl);
      // Just pre-fill the URL, let user click to process
    }
  }, [location.search]);

  const extractPinterestInfo = async (pinterestUrl) => {
    try {
      // Basic validation for Pinterest URL
      if (!pinterestUrl.includes('pinterest.com') && !pinterestUrl.includes('pin.it')) {
        throw new Error('Please enter a valid Pinterest URL');
      }

      // Call our backend API to handle URL resolution and basic metadata extraction
      const response = await fetch('/api/pinterest/extract-pin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: pinterestUrl }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to extract Pinterest information');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to extract Pinterest information');
      }

      return {
        pinId: result.data.pinId,
        embedUrl: result.data.embedUrl,
        originalUrl: result.data.originalUrl,
        expandedUrl: result.data.resolvedUrl,
        imageUrl: result.data.imageUrl,
        pinTitle: result.data.title,
        pinDescription: result.data.description
      };
    } catch (error) {
      throw new Error(error.message || 'Failed to process Pinterest URL');
    }
  };

  // Debounced URL processing
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (url.trim() && (url.includes('pinterest.com') || url.includes('pin.it'))) {
        setProcessingUrl(true);
        setError('');
        setPinterestEmbed('');
        setExtractedInfo(null);

        try {
          const info = await extractPinterestInfo(url);
          setExtractedInfo(info);
          setPinterestEmbed(info.embedUrl);
        } catch (err) {
          setError(err.message);
        } finally {
          setProcessingUrl(false);
        }
      } else if (url.trim() === '') {
        setError('');
        setPinterestEmbed('');
        setExtractedInfo(null);
      }
    }, 300); // Short delay to ensure full paste completes

    return () => clearTimeout(timeoutId);
  }, [url]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    
    setLoading(true);
    setError('');

    try {
      const info = await extractPinterestInfo(url);
      setExtractedInfo(info);
      setPinterestEmbed(info.embedUrl);
      
      // Show navigation loading animation
      setNavigationLoading(true);
      
                // Navigate to the remix page after successful extraction
          if (info.pinId) {
            // Add a small delay to show the loading animation
            setTimeout(() => {
              navigate(`/pinterest-remix/${info.pinId}`, {
                state: { styleOption: selectedStyleOption }
              });
            }, 100); // Reduced delay to 100 ms
          }
    } catch (err) {
      setError(err.message);
      setNavigationLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUrlChange = (e) => {
    setUrl(e.target.value);
    if (error) setError('');
  };

  const handlePinSelect = (pinUrl) => {
    setUrl(pinUrl);
    setError('');
    // The useEffect will automatically process the URL
  };

  return (
    <Box sx={{ 
      bgcolor: 'background.paper',
      height: '90dvh',
      paddingTop: '10dvh',
      overflowY: 'scroll',
      boxSizing: 'content-box',
    }}>
      {/* Navigation Loading Overlay */}
      {navigationLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
          }}
        >
          <CircularProgress 
            size={60} 
            sx={{ 
              color: '#E60023',
              mb: 3,
            }} 
          />
          <Typography 
            variant="h6" 
            fontWeight="bold" 
            sx={{ 
              mb: 1,
              color: theme.palette.primary.main,
            }}
          >
            Preparing Your Style...
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: theme.palette.text.secondary,
              textAlign: 'center',
              maxWidth: '300px',
            }}
          >
            We're preparing your Pinterest pin for style analysis. This will take just a moment...
          </Typography>
        </Box>
      )}

      <Container maxWidth="md" sx={{ py: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Pinterest sx={{ fontSize: 40, color: '#E60023', mb: 1 }} />
          {/*<Typography
            variant="h4"
            fontWeight="bold"
            sx={{ mb: 1, color: theme.palette.primary.main }}
          >
            Style Your Pin.
          </Typography>*/}
          <Typography variant="body1" sx={{ mb: 1, color: theme.palette.text.secondary }}>
          <span style={{ fontWeight: 'bold' }}>Drop</span> a Fashion Pin to <span style={{ fontWeight: 'bold' }}>style</span> the perfect look.
          </Typography>
        </Box>

        {/* Combined Pinterest Embed and Form Section */}
        <Paper elevation={2} sx={{ p: 2, mb: 1, borderRadius: 2 }}>
          {/* Pinterest Embed Section */}
          {extractedInfo && (
            <>
              {/*<Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Add your twist. Make it yours.
              </Typography>*/}
              
              <Box
                sx={{
                  width: '100%',
                  minHeight: '400px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                  mb: 1,
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    zIndex: 1,
                  }}
                  id="pinterest-loading"
                >
                  <CircularProgress 
                    size={40} 
                    sx={{ 
                      color: '#E60023',
                      mb: 2,
                    }} 
                  />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                    }}
                  >
                    Loading Pin...
                  </Typography>
                </Box>
                <iframe
                  src={pinterestEmbed}
                  width="100%"
                  height="400"
                  frameBorder="0"
                  scrolling="no"
                  allowTransparency={true}
                  title="Inspired by Pinterest"
                  style={{ pointerEvents: 'none' }}
                  onLoad={() => {
                    const loadingElement = document.getElementById('pinterest-loading');
                    if (loadingElement) {
                      loadingElement.style.display = 'none';
                    }
                  }}
                />
              </Box>
              
              {extractedInfo.pinTitle && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Pin Title
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {extractedInfo.pinTitle}
                  </Typography>
                </Box>
              )}
              
              {extractedInfo.pinDescription && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Description
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {extractedInfo.pinDescription}
                  </Typography>
                </Box>
              )}
              
              {extractedInfo.fashionVibes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Extracted Fashion Vibes
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.entries(extractedInfo.fashionVibes).map(([key, value]) => {
                      if (key !== 'caption' && key !== 'confidence' && value) {
                        return (
                          <Chip
                            key={key}
                            label={`${key}: ${value}`}
                            size="small"
                            sx={{ backgroundColor: '#e3f2fd' }}
                          />
                        );
                      }
                      return null;
                    })}
                  </Box>
                </Box>
              )}
              
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* Form Section */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Paste URL or choose a pin below…"
              placeholder="https://www.pinterest.com/pin/... or https://pin.it/..."
              value={url}
              onChange={handleUrlChange}
              variant="outlined"
              sx={{ mb: 1 }}
              disabled={loading}
              InputProps={{
                endAdornment: processingUrl ? (
                  <CircularProgress size={20} sx={{ color: '#E60023' }} />
                ) : null,
              }}
            />
            
            {/* Style Option Chips - Show when URL is populated */}
            {url.trim() && (url.includes('pinterest.com') || url.includes('pin.it')) && (
              <Box 
                sx={{ 
                  mb: 2,
                  mt: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                <Typography variant="body2" fontWeight="medium" color="text.secondary">
                  What would you like to tweak?
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    overflowX: 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': {
                      height: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#E60023',
                      borderRadius: '3px',
                    },
                  }}
                >
                  {[
                    { label: 'Color', value: 'color' },
                    { label: 'Pattern', value: 'pattern' },
                    { label: 'Palette', value: 'palette' },
                    { label: 'Fabric', value: 'fabric' },
                    { label: 'Silhouette', value: 'silhouette' }
                  ].map((option) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onClick={() => setSelectedStyleOption(option.value)}
                      sx={{
                        backgroundColor: selectedStyleOption === option.value ? '#E60023' : 'rgba(230, 0, 35, 0.1)',
                        color: selectedStyleOption === option.value ? 'white' : '#E60023',
                        fontWeight: selectedStyleOption === option.value ? 'bold' : 'medium',
                        transition: 'all 0.2s ease-in-out',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: selectedStyleOption === option.value ? '#CC001F' : 'rgba(230, 0, 35, 0.2)',
                          transform: 'scale(1.05)',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || !url.trim() || processingUrl}
              sx={{
                bgcolor: '#E60023',
                '&:hover': {
                  bgcolor: '#CC001F',
                },
                mb: 2,
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Style This Pin'
              )}
            </Button>
          </form>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>

        {/* Turn Inspiration into Style Card with Carousel */}
        <Paper elevation={1} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Or choose a pin below
          </Typography>
          <Typography variant="body2" paragraph sx={{ mb: 3 }}>
          Remix the style. And create that perfect look.
          </Typography>
          
          <PinterestPinCarousel onPinSelect={handlePinSelect} />
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
};

export default PinterestStylePinPage; 